import ErrorIcon from '@mui/icons-material/Error';
import CommentsIcon from '@mui/icons-material/ModeComment';
// Permissions checked separately
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { Button, Chip, Typography } from '@mui/material';
import isFunction from 'lodash/isFunction';
import { RaRecord, useGetList, useRecordContext, useTranslate } from 'react-admin';

import { GetCommentOneResponse } from '@Common/types/bo/comments';

import { useCanAccess } from '@sh4res/bo-common/hooks';

export type CommentsProps = {
  source: string;
  target: keyof GetCommentOneResponse | ((record: unknown) => keyof GetCommentOneResponse);
  getFilterValue?: (value?: string) => unknown;
};

export function CommentsButton(
  props: CommentsProps & { onOpen: () => void; 'data-testid'?: string },
) {
  const record = useRecordContext<RaRecord>();
  const translate = useTranslate();
  const canAccess = useCanAccess({
    resource: 'bo_comments',
    action: 'list',
  });

  const effectiveTarget = isFunction(props.target) ? props.target(record) : props.target;

  const filterValue = props.getFilterValue?.() ?? record?.[props.source];

  const { total, isLoading, error } = useGetList('bo_comments', {
    filter: { [effectiveTarget]: filterValue },
    pagination: { perPage: 1, page: 1 },
  });

  if (!record || !canAccess) {
    return null;
  }

  // eslint-disable-next-line no-nested-ternary -- simple to understand
  const count = isLoading ? (
    ''
  ) : // oxlint-disable-next-line unicorn/no-nested-ternary
  error ? (
    <ErrorIcon color="error" fontSize="small" titleAccess="error" />
  ) : (
    total
  );

  return (
    <Button
      variant="contained"
      color={total !== undefined && total > 0 ? 'error' : 'info'}
      disableElevation={true}
      size="small"
      sx={{
        height: '30px',
        fontWeight: 'bold',
      }}
      startIcon={<CommentsIcon />}
      onClick={props.onOpen}
      data-testid={props['data-testid']}>
      <>
        {translate('general.comments.label')}
        <Chip
          size="small"
          sx={{
            height: '20px',
            marginLeft: '5px',
            backgroundColor: '#F8F8F8',
            '& .MuiChip-label': {
              padding: 0,
            },
          }}
          label={
            <Typography
              sx={{
                fontWeight: 600,
              }}
              component="span"
              variant="body2">
              {count}
            </Typography>
          }
        />
      </>
    </Button>
  );
}
